import styled from "styled-components/macro";
import { typography } from "src/utils/styledComponents/typography";
import { Link } from "react-router-dom";
import { LARGE_SCREEN, SMALL_LAPTOP } from "src/utils/constants/media";
import { Button as AntButton } from "antd";
import { colors } from "src/theme/colors";
import { line } from "src/theme/ui";

export const Container = styled.div<{
  $disableDetails?: boolean;
  isMobile?: boolean;
  $plain?: boolean;
}>`
  display: grid;
  /**
   * Tracks that can be NARROWER than the content in them.
   *
   * This was a 328px track beside an AUTO one, and an auto track is sized
   * from its content's minimum — so a page whose content had a wide minimum
   * simply made the track wider than the grid, and the grid wider than the
   * window.
   * Measured on the smartfolio list at a 1000px window: the content track
   * grew to 466 beside a 270 details track, the main pane came out 736 in a
   * 696 container, and the browser grew a horizontal scrollbar. Every page
   * on this layout could do the same.
   *
   * A 1fr track with a zero floor takes the room that is there and hands
   * the overflow problem to the content, where it belongs — the page can
   * then decide what to drop, which is a decision a card can make and a
   * grid track cannot.
   *
   * And a page with no details column gets ONE track rather than two with
   * an area name repeated across them: the second track was implicit, so it
   * sized itself from the content as well.
   */
  grid-template-columns: ${(props) =>
    props.$disableDetails
      ? "minmax(0, 1fr)"
      : "minmax(0, 328px) minmax(0, 1fr)"};
  grid-template-rows: auto;
  grid-template-areas: ${(props) =>
    props.$disableDetails ? "'content'" : "'details content'"};
  width: 100%;
  /* Not hidden on a plain page. An overflow value on ANY ancestor makes
     that ancestor the scrollport a sticky descendant resolves against — and
     this one never scrolls, so anything sticky inside it had nowhere to
     travel and simply sat still. The window is the scroller; this has to
     stay out of the way of it. */
  overflow: ${(props) => (props.$plain ? "visible" : "hidden")};
  ${typography.CustomText("1em")};

  @media (max-width: ${LARGE_SCREEN}px) {
    grid-template-columns: ${(props) =>
      props.$disableDetails
        ? "minmax(0, 1fr)"
        : "minmax(0, 270px) minmax(0, 1fr)"};
  }

  /* One column below a laptop, and the details ride above the content
     rather than beside it. */
  @media (max-width: ${SMALL_LAPTOP}px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: ${(props) =>
      props.$disableDetails ? "'content'" : "'details' 'content'"};
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 1em;
  padding-bottom: 1em;
  margin-bottom: 1em;
  background-color: #fff;
  border-radius: 5px;
`;

export const Wrapper = styled.div`
  display: flex;
  min-height: 90vh;
  background-color: #fff;
  border-radius: 12px;
  padding: 1em;
`;

export const Back = styled(Link)`
  display: flex;
  align-items: center;
  padding-left: 1em;
  color: ${colors.primary[500]};
  font-size: 1.2em;
  font-weight: 600;
  cursor: pointer;
  grid-area: back;
  background-color: #fff;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
`;

export const Icon = styled.img`
  filter: invert(37%) sepia(35%) saturate(6278%) \hue-rotate(207deg)brightness(
      103%
    ) contrast(93%);
  transform: rotate(180deg);
`;

export const GoBackText = styled.span`
  margin-left: 1.5em;
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.25em;
  font-weight: 600;
  grid-area: title;
  background-color: #fff;
  padding-left: 2em;
  padding-right: 2em;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  justify-content: space-between;
`;

export const Button = styled(AntButton)`
  &.ant-btn {
    &.ant-btn-link {
      background: #f2a20c;
      border-color: #f2a20c;
      color: white;
      border-radius: 5px;

      &:hover,
      &:focus {
        background: #f7be54;
        border-color: #f7be54;
      }
    }
  }
`;

/**
 * The side panel — a navigation rail, or a balance summary.
 *
 * It was a periwinkle slab: primary[10], which on this tenant is a real brand
 * tint rather than a neutral. Sitting inside a white container, against a
 * white content pane, it read as a coloured region of one surface rather than
 * as a panel of its own — and anything placed on it then had to be coloured
 * to survive, which is how the deposit and withdraw balances ended up
 * accent-on-accent.
 *
 * A panel, like every other panel in the product: white, one hairline, the
 * card radius. The tinted ground it used to provide comes from the page
 * behind it instead.
 */
export const Details = styled.div`
  background: #fff;
  border: 1px solid ${line.hairline};
  border-radius: 12px;
  transition: all 0.2s ease-in-out;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

/* Transparent, with air on BOTH sides. It was a white box with padding on
   three sides only, so the rail was glued to the content pane and the two
   cards met with no gap — one wide surface split by a line, rather than two
   objects. */
export const DetailsContainer = styled.div`
  grid-area: details;
  background: transparent;
  padding: 0 16px 0 0;

  @media (max-width: ${SMALL_LAPTOP}px) {
    padding: 0;
    display: none;
  }
`;

export const Main = styled.main<{
  $detailsDisabled?: boolean;
  $plain?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  grid-area: content;
  /* A page whose content is already cards does not want a card of its own:
     a white slab under a white card is the same box drawn twice, and every
     hairline inside it then reads as a division of a surface rather than the
     edge of an object. Pages that are a bare form still need the surface, so
     it is opted OUT of rather than removed. */
  padding: ${(props) => (props.$plain ? "0" : "20px")};
  background-color: ${(props) => (props.$plain ? "transparent" : "white")};
  border: ${(props) => (props.$plain ? "0" : `1px solid ${line.hairline}`)};
  width: 100%;
  /* Its own card on every side. It used to square off its left edge so it
     could butt against the details column; with the two panels now separated
     by a gap, a squared edge is just a card missing two corners. */
  border-radius: ${(props) => (props.$plain ? "0" : "12px")};
  /* Same reason as the container above: a scrollport that does not scroll
     is still a scrollport, and it is what a sticky bar inside it measures
     itself against. */
  overflow-y: ${(props) => (props.$plain ? "visible" : "auto")};
  ::-webkit-scrollbar {
    display: none;
  }
  /* @media (max-width: ${SMALL_LAPTOP}px) {
    padding-top: 1rem;
  } */
`;

export const AnchorContent = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0;
  width: 100%;
  border-right: 2px solid #eee;
`;

Container.defaultProps = {
  isMobile: false,
};
